import { formatPrice } from "@/utils/money";
import { cn } from "@repo/design-system/utils";
import { memo } from "react";

/** A name that already says what kind of service it is. */
const NAMES_THE_SERVICE = /\b(shipping|delivery|freight|postage|post)\b/i;

/**
 * How to name the thing that goes free: the qualifying option's own name, so
 * the promise matches the offer.
 *
 * A threshold zeroes *one* shipping option - typically standard. Saying "free
 * shipping" flat over-promises to a buyer sitting on Express, whose charge the
 * threshold does not touch. Each supplier names their own options, so the name
 * is taken from the data rather than hardcoded to "standard".
 *
 * Case is preserved (supplier names can be proper nouns) and " shipping" is
 * appended only when the name doesn't already say what it is, so "Standard"
 * reads "free Standard shipping" while "Standard Delivery" isn't doubled up.
 * A name that already contains "free" would read "free Free Shipping", so it
 * falls back to the plain word.
 */
export function freeShippingLabel(optionName?: string): string {
  const name = optionName?.trim();
  if (!name || /\bfree\b/i.test(name)) return "shipping";
  return NAMES_THE_SERVICE.test(name) ? name : `${name} shipping`;
}

export interface FreeShippingPromptProps {
  /**
   * Amount needed to reach free shipping (store currency's major unit, the
   * same unit as cart totals)
   */
  amountToFreeShipping?: number;
  /**
   * The free shipping threshold (store currency's major unit)
   */
  freeShippingThreshold?: number;
  /**
   * What crossing the threshold saves - the qualifying option's paid base
   * price (store currency's major unit). When provided, the copy names the
   * payoff ("save $X on shipping") instead of just asking for more spend.
   */
  shippingSavings?: number;
  /**
   * The name of the shipping option the threshold applies to (typically the
   * standard service). Used to qualify the copy - see
   * {@link freeShippingLabel}. Omit to keep the unqualified wording.
   */
  shippingOptionName?: string;
  /**
   * Whether this instance announces itself via `aria-live`.
   *
   * Default `true`. Set `false` when the same threshold is already
   * announced by another visible instance for the same seller-cart on the
   * page (e.g. Cart's middle-column accordion and its right-rail order
   * summary both show this cart's meter at once) — otherwise a screen
   * reader announces the identical sentence twice. The visual box still
   * renders; only the live-region behaviour is suppressed.
   */
  announce?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Displays a prompt showing how much more the customer needs to spend
 * to qualify for free shipping.
 *
 * Hides when:
 * - No threshold is set (freeShippingThreshold is undefined)
 * - Free shipping is already achieved (amountToFreeShipping is 0 or undefined)
 */
export const FreeShippingPrompt = memo(function FreeShippingPrompt({
  amountToFreeShipping,
  freeShippingThreshold,
  shippingSavings,
  shippingOptionName,
  announce = true,
  className
}: FreeShippingPromptProps) {
  // Don't show if no threshold or already qualified
  if (
    !freeShippingThreshold ||
    !amountToFreeShipping ||
    amountToFreeShipping <= 0
  ) {
    return null;
  }

  // Format amount for display (formatPrice already includes the currency symbol)
  const formattedAmount = formatPrice(amountToFreeShipping);

  // Name the payoff when we know it - a trade reads better than a demand -
  // and name the service it applies to, since only that one option goes free.
  // Plain words ("for", "save") carry the relationship between the two
  // figures: symbols read as ambiguous to buyers, and screen readers drop them
  // at default verbosity inside this `aria-live` region.
  // Falls back to the plain phrasing rather than inventing a figure.
  const label = freeShippingLabel(shippingOptionName);

  const copy = shippingSavings
    ? `Add ${formattedAmount} for free ${label} (save ${formatPrice(shippingSavings)})`
    : `Add ${formattedAmount} for free ${label}`;

  return (
    <div
      role="status"
      aria-live={announce ? "polite" : "off"}
      className={cn(
        "text-xs font-normal text-amber-600",
        "rounded bg-amber-50 px-2 py-1",
        "border border-amber-200",
        className
      )}
    >
      {copy}
    </div>
  );
});
